import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetNetworksPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/inputs/get-networks-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetNetworksPaginatedQuery } from '@app/core/service/cpms-bo-network.service';
import { NETWORK_LIST_SORT_FIELD } from '@app/feature/network/network-list/network-list.config';

import {
  CustomerNetworkListContextProvider,
  useCustomerContext,
  useCustomerNetworkListContext,
} from '../customer.context';
import * as css from './customer-network-list.module.scss';
import { CustomerNetworkListView } from './customer-network-list.view';

export const CustomerNetworkListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
  } = useCustomerNetworkListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetNetworksPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? NETWORK_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetNetworksPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const {
    Component: CustomerNetworkListViewProtected,
    status: resourceStatus,
  } = withProtectedResource(CustomerNetworkListView, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.NETWORKS,
        action: ActionsByResource.NETWORKS.READ,
      }),
    transformDataToResource: transformPaginatedResponse,
  })({
    paginationLimit: getPaginatedQueryParameters.limit,
    paginationLimitList: getPaginatedLimitList,
    paginationState,
    paginationSorting: [
      {
        desc: getPaginatedQueryParameters.sortOrder === -1,
        id: getPaginatedQueryParameters.sortField ?? NETWORK_LIST_SORT_FIELD,
      },
    ],
    onPaginationLimitChange: handlePaginationLimitChange,
    onPaginationPageChange: handlePaginationPageChange,
    onPaginationSortingChange: handlePaginationSortingChange,
  });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack justifyContent="end" gap={GapEnum.M}>
          <div />
        </Stack>
      )}
      {CustomerNetworkListViewProtected}
    </Stack>
  );
};

export const CustomerNetworkListControllerWithContext =
  (): React.JSX.Element => {
    const customer = useCustomerContext();

    return (
      <CustomerNetworkListContextProvider data={{ customer }}>
        <CustomerNetworkListController />
      </CustomerNetworkListContextProvider>
    );
  };
